import { useState } from "react";
import { Sparkles, Loader2, Wand2, Cpu, Info, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtRp, fmtRpShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { SwitchRow } from "@/components/common/Sel";
import { Segmented, InfoNote } from "@/components/common/Cards";

export function AutoOptimizeDialog({ open, onOpenChange, companyIds, selectedCompanies, onApply }) {
  const [mode, setMode] = useState("optimizer");
  const [maxShare, setMaxShare] = useState(30);
  const [allowRealokasi, setAllowRealokasi] = useState(false);
  const [explain, setExplain] = useState(true);
  const [running, setRunning] = useState(false);
  const [resp, setResp] = useState(null);

  const run = async () => {
    setRunning(true);
    setResp(null);
    try {
      const { data } = await api.post("/equalizer/optimize", {
        company_ids: companyIds,
        mode,
        max_share: maxShare / 100,
        allow_realokasi: allowRealokasi,
        explain,
      });
      setResp(data);
      if (!data.transfers?.length) {
        toast.info(data.notice || "Tidak ditemukan pemindahan yang menguntungkan dengan parameter ini.");
      }
    } catch (e) {
      toast.error(errMsg(e, "Gagal menjalankan auto-optimize"));
    } finally {
      setRunning(false);
    }
  };

  const close = () => {
    if (running) return;
    onOpenChange(false);
    setResp(null);
  };

  const apply = () => {
    if (!resp?.transfers?.length) return;
    onApply(resp.transfers);
    toast.success(`${resp.transfers.length} pemindahan diterapkan ke skenario.`);
    close();
  };

  const nameOf = (id) => selectedCompanies.find((c) => c.id === id)?.name || "-";
  const g = resp?.result?.group;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="eq-opt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" /> Rekomendasi otomatis ekualisasi
          </DialogTitle>
          <DialogDescription>
            Mesin hitung mencari kombinasi pemindahan antar-PT yang paling hemat pajak secara legal; AI menyusun penjelasan dan catatan risiko.
          </DialogDescription>
        </DialogHeader>

        {!resp && (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">Mode</div>
              <Segmented
                value={mode}
                onChange={setMode}
                testid="eq-opt-mode"
                options={[
                  { value: "optimizer", label: "Mesin hitung" },
                  { value: "ai", label: "AI usulkan" },
                ]}
              />
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                {mode === "optimizer"
                  ? "Python mencari kombinasi optimal secara matematis (cepat, deterministik). Direkomendasikan."
                  : "AI menyusun usulan pemindahan lalu divalidasi oleh engine simulasi. Konsumsi token AI."}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between text-[12px] mb-1.5">
                <span className="text-muted-foreground">Batas pemindahan per pos</span>
                <span className="num text-foreground">maks {maxShare}%</span>
              </div>
              <Slider
                value={[maxShare]}
                onValueChange={(v) => setMaxShare(v[0])}
                min={5}
                max={100}
                step={5}
                className="mt-1"
                data-testid="eq-opt-maxshare"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Tiap pos pendapatan/beban hanya digeser maksimal {maxShare}% dari nominalnya. Semakin kecil, semakin aman.
              </p>
            </div>

            <SwitchRow
              label="Izinkan realokasi (ganti kepemilikan pos)"
              hint="Mode realokasi memindahkan pos sepenuhnya; mode 'tagihan' lebih aman karena hanya recharge antar-PT."
              checked={allowRealokasi}
              onChange={setAllowRealokasi}
              testid="eq-opt-realokasi"
            />
            <SwitchRow
              label="Minta AI menjelaskan hasil"
              hint="AI menyusun ringkasan alasan, risiko transfer pricing, dan dokumen yang perlu disiapkan."
              checked={explain}
              onChange={setExplain}
              testid="eq-opt-explain"
            />
          </div>
        )}

        {resp && (
          <div className="space-y-4" data-testid="eq-opt-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border bg-[hsl(220_12%_8%)] p-3">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Pemindahan</div>
                <div className="num text-xl mt-1">{resp.transfers?.length || 0}</div>
              </div>
              <div className="rounded-lg border border-border bg-[hsl(220_12%_8%)] p-3">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Hemat bersih grup</div>
                <div className={`num text-xl mt-1 ${(g?.hemat_bersih || 0) > 0 ? "text-[hsl(160_55%_62%)]" : (g?.hemat_bersih || 0) < 0 ? "text-[hsl(0_72%_68%)]" : "text-foreground"}`}>
                  {g?.hemat_bersih ? fmtRpShort(g.hemat_bersih) : "Rp 0"}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-[hsl(220_12%_8%)] p-3">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Mode</div>
                <div className="text-xs mt-1 flex items-center gap-1">
                  {resp.mode === "optimizer" ? <Cpu className="h-3.5 w-3.5 text-primary" /> : <Sparkles className="h-3.5 w-3.5 text-primary" />}
                  {resp.mode === "optimizer" ? "Mesin hitung" : "AI usulkan"}
                </div>
              </div>
            </div>

            {resp.notice && (
              <InfoNote testid="eq-opt-notice">{resp.notice}</InfoNote>
            )}

            {resp.transfers?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">Usulan pemindahan</div>
                <ul className="space-y-1.5" data-testid="eq-opt-transfers">
                  {resp.transfers.map((t, i) => (
                    <li key={t.id || i} className="rounded-lg border border-border bg-[hsl(220_10%_7%)] px-3 py-2 text-sm flex flex-wrap items-center gap-2">
                      <ArrowRightLeft className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className={t.section === "pendapatan" ? "text-[hsl(160_55%_62%)]" : "text-[hsl(0_72%_68%)]"}>{t.item_name}</span>
                      <span className="text-muted-foreground">{nameOf(t.from_id)} → {nameOf(t.to_id)}</span>
                      <span className="num text-primary">{fmtRp(t.amount)}</span>
                      <Badge variant="outline" className="text-[10px] border-border">
                        {t.mode === "tagihan" ? `Tagihan · ${t.tx_type}` : "Realokasi"}
                      </Badge>
                      {t.note && <span className="text-[11px] text-muted-foreground w-full pl-6">“{t.note}”</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {resp.explanation && (
              <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_28%_6%)] p-3.5" data-testid="eq-opt-explanation">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-primary font-medium mb-2">
                  <Sparkles className="h-3.5 w-3.5" /> Penjelasan AI
                </div>
                <div className="text-[13px] leading-relaxed text-foreground/90 whitespace-pre-wrap">{resp.explanation}</div>
                {resp.ai?.model && (
                  <div className="text-[10px] text-muted-foreground mt-2 num">
                    {resp.ai.model} · {resp.ai.total_tokens || 0} token
                  </div>
                )}
              </div>
            )}

            {(resp.result?.warnings || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5" /> Catatan kepatuhan
                </div>
                <ul className="space-y-1">
                  {(resp.result.warnings || []).slice(0, 8).map((w, i) => (
                    <li key={i} className="text-[11px] text-muted-foreground leading-relaxed">
                      <Badge variant="outline" className={`mr-1.5 text-[9px] ${w.level === "danger" ? "border-[hsl(0_45%_35%)] text-[hsl(0_72%_68%)]" : w.level === "warn" ? "border-[hsl(35_60%_40%)] text-[hsl(35_80%_68%)]" : "border-border"}`}>
                        {w.level}
                      </Badge>
                      {w.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!resp ? (
            <>
              <Button variant="ghost" onClick={close} disabled={running} data-testid="eq-opt-cancel">Batal</Button>
              <Button onClick={run} disabled={running} className="press" data-testid="eq-opt-run">
                {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
                {running ? "Menghitung…" : "Jalankan"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setResp(null)} data-testid="eq-opt-redo">Ubah parameter</Button>
              <Button variant="outline" onClick={close} data-testid="eq-opt-close">Tutup</Button>
              <Button
                onClick={apply}
                disabled={!resp.transfers?.length}
                className="press"
                data-testid="eq-opt-apply"
              >
                Terapkan ke skenario
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
